/**
 * The map's own answer to "could a standing player be here?"
 *
 * GoldSrc maps carry three collision hulls in the CLIPNODES lump, one per
 * player size. Hull 1 is the standing player, and it is the ONLY place a CLIP
 * brush exists: hlbsp strips clip faces out of the face lump, so the invisible
 * barriers that fence off roofs and ledges are invisible to a converter that
 * reads faces — which is why walking off the roof near A dropped you through
 * the world.
 *
 * The hull is DILATED by the player's half-extents, so the engine traced a
 * point (the player's origin) rather than a box. That makes a point query
 * against it mean something much more useful than "is there a wall here": it
 * means "is this a position the map allowed a standing player to occupy".
 *
 * Used as a VALIDATOR, not as collision. Movement still resolves against the
 * triangle Octree, because the dilated hull would make every wall 0.4 m thick
 * and change how the game feels. This only answers, after the fact, whether
 * somebody has ended up somewhere the map never allowed — see
 * PlayerController._recoverIfLost.
 */

/** GoldSrc contents. Anything at or below SOLID is not standable space. */
const SOLID = -2;

export class ClipHull {
  /** @param {{head:number, lift:number, nodes:number[], planes:number[]}} data */
  constructor(data) {
    this.head = data.head | 0;
    this.lift = data.lift ?? 0.9144;
    // Typed, because this is queried per player per frame and a plain array of
    // 25 000 numbers is a pointer chase per element.
    this.nodes = Int32Array.from(data.nodes);
    this.planes = Float32Array.from(data.planes);
  }

  /**
   * Contents at a point already in world space (Y-up metres).
   * @returns {number} -1 empty, -2 or lower solid.
   */
  at(x, y, z) {
    const N = this.nodes;
    const P = this.planes;
    let n = this.head;
    // A negative index is a leaf, so the descent ends the moment it goes
    // negative. The step cap only guards against a malformed tree — a real one
    // is a few dozen levels deep, not four thousand.
    for (let steps = 0; n >= 0 && steps < 4096; steps++) {
      const o = n * 3;
      const p = N[o] * 4;
      const d = P[p] * x + P[p + 1] * y + P[p + 2] * z - P[p + 3];
      n = N[o + (d >= 0 ? 1 : 2)];
    }
    return n;
  }

  /**
   * Is a player standing with their FEET here somewhere the map forbids?
   *
   * The lift is what makes this the right question: the hull traces the centre
   * of a dilated box, so testing the feet themselves would report solid every
   * time somebody stood on a floor.
   */
  forbidsFeet(x, y, z) { return this.at(x, y + this.lift, z) <= SOLID; }

  /**
   * Is somebody standing here somewhere the map genuinely forbids?
   *
   * NOT the same question as `forbidsFeet`, and the difference is the whole
   * reason this is safe to act on. Two artefacts make the raw hull disagree
   * with legitimate play, and both were measured rather than guessed
   * (tools/hull-probe.mjs, three bot rounds on Dust II):
   *
   *  - The hull is dilated by the player's half-width, 16 units (0.406 m),
   *    while our capsule radius is 0.34 m — so anybody pressed against a wall
   *    is a few centimetres inside the dilated wall column. Judging on one
   *    point flagged 5.8% of ordinary play, most of it bots in cover.
   *  - Hull 1's floor over a staircase is a coarse staircase of its own,
   *    offset from the visible treads, so standing on a tread edge can put the
   *    origin inside a hull step.
   *
   * Requiring solid in every horizontal direction AND at two heights removes
   * both: open ground is one step away from a wall-hugger, and a step is 40 cm
   * from clear air. Somewhere the map actually fenced off — a roof under a clip
   * brush — is solid for metres in every direction.
   *
   * What that leaves, measured over three bot rounds per run: between zero and
   * three distinct spots per run out of ~8700 grounded samples, on both Dust II
   * and Assault, and the figure does NOT improve monotonically with wider
   * rings — the run-to-run variance dominates it. Some of those are the hull
   * being RIGHT, because bots mantle onto ledges the original map fenced off
   * too. So the residual is treated as irreducible and handled by making the
   * CONSEQUENCE cheap rather than by chasing the discriminator: see
   * PlayerController._lost, which acts instantly only on somebody already
   * falling and otherwise waits three seconds.
   */
  forbidsStanding(x, y, z) {
    if (this._free(x, y, z)) return false;
    // Two rings, not one. A single ring at 0.45 m left two spots on Assault
    // where a bot in cover read as out of bounds — marginal geometry where the
    // hull is a little tighter than our triangles. Somewhere genuinely fenced
    // off is solid for metres, so asking again at 0.9 m costs four more tree
    // descents and removes that class without missing a real ledge.
    for (const r of RINGS) {
      if (this._free(x + r, y, z) || this._free(x - r, y, z)
        || this._free(x, y, z + r) || this._free(x, y, z - r)) return false;
    }
    return true;
  }

  /** Empty at this column, allowing a step of vertical slack. */
  _free(x, y, z) {
    return !this.forbidsFeet(x, y, z) || !this.forbidsFeet(x, y + STEP, z);
  }
}

/**
 * How far out to look for open ground. The first ring is a shade over the
 * dilation gap (0.406 m); the second is far enough that only a genuinely
 * fenced-off surface has no way out.
 */
const RINGS = [0.45, 0.9];
/** Vertical slack, for the hull's coarse staircases. */
const STEP = 0.4;

/** Build one from a map payload, or null when the map has no hull. */
export function clipHullFrom(data) {
  const h = data?.hull;
  return h && h.nodes?.length ? new ClipHull(h) : null;
}
